import type { ButtonHTMLAttributes, Ref } from 'react';

import type { VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/ui/cn';
import type { IconName } from '@/shared/ui/Icon';
import { Icon } from '@/shared/ui/Icon';
import { CountBadge } from '@/shared/ui/CountBadge';
import { ICON_GLYPH, iconButtonVariants } from '@/shared/ui/icon-button-variants';

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> &
  VariantProps<typeof iconButtonVariants> & {
    name: IconName;
    /** Obligatoire : une icône seule ne se lit pas. Sert aussi d'info-bulle. */
    label: string;
    /** Compteur rouge : non-lus seulement (notifications, messages, panier). */
    badge?: number;
    /** Icône active (pleine), pour un bouton à bascule : signet, son. */
    pressed?: boolean;
    iconClassName?: string;
    ref?: Ref<HTMLButtonElement>;
  };

/**
 * Icône cliquable. La cible fait toujours au moins 32 px, 40 par défaut : un
 * doigt ne rate pas une icône de 24.
 */
export function IconButton({
  name,
  label,
  badge,
  variant,
  size,
  pressed,
  className,
  iconClassName,
  type,
  ref,
  title,
  ...rest
}: IconButtonProps) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      aria-pressed={pressed}
      title={title ?? label}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...rest}
    >
      <Icon name={name} size={ICON_GLYPH[size ?? 'md']} className={iconClassName} />
      {badge !== undefined && badge > 0 ? (
        <CountBadge value={badge} className="absolute top-0.5 right-0.5" />
      ) : null}
    </button>
  );
}
